// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { formatCell } from '../../api/format'
import { LayoutList, Plus, Play, Trash2, Loader2, AlertCircle, Pencil } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import CategoryBadge from '../../components/common/CategoryBadge'
import SearchableSelect from '../../components/common/SearchableSelect'

export default function ViewBuilderPage() {
  const [views, setViews] = useState<api.PharmacyView[]>([])
  const [catalog, setCatalog] = useState<api.ViewFieldCatalogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  // CV-5: the views live in a dropdown; the selected one shows its Run/Edit/Delete card.
  const [selectedId, setSelectedId] = useState('')
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [previewData, setPreviewData] = useState<{ columns: string[]; columns_meta: { label: string; source_table: string; column_name: string }[]; rows: Record<string, any>[]; runtime_ms: number } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [catColors, setCatColors] = useState<Record<string, string | null>>({})
  const queryClient = useQueryClient()

  const reload = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      // P-VD-1: dupCheck computes identical-view badges server-side (settings list only).
      const [v, c] = await Promise.all([api.listViews(undefined, { dupCheck: true }), api.getViewCatalog()])
      setViews(v.items); setCatalog(c.items)
    } catch (e: any) { setError(friendlyError(e)) }
    finally { setLoading(false) }
  }, [])
  useEffect(() => { reload() }, [reload])

  // CV-5: after any create/edit/delete, ALSO invalidate the portal layout's ['views'] query
  // so the Prescriptions tab bar picks the change up now, not after its 120s staleTime.
  const afterMutation = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['views'] })
    return reload()
  }, [queryClient, reload])

  // A deleted (or externally removed) selection must not survive a reload.
  useEffect(() => {
    if (selectedId && !loading && !views.some(v => v.id === selectedId)) setSelectedId('')
  }, [views, selectedId, loading])

  const executeView = async (id: string) => {
    setPreviewId(id); setPreviewLoading(true); setPreviewData(null)
    try {
      const d = await api.executeView(id, { limit: 100 })
      const meta = d.columns_meta ?? []
      setPreviewData({ columns: d.columns, columns_meta: meta, rows: d.rows, runtime_ms: d.runtime_ms })
      // D-ASP-VB-1 item 4: color DrugReference category cells in the preview too.
      if (meta.some(m => m.source_table === 'drug_reference' && m.column_name === 'category'))
        api.listDrugCategories()
          .then(r => setCatColors(Object.fromEntries(r.items.map(c => [c.name, c.color ?? null]))))
          .catch(() => {})
    } catch (e: any) { setError(friendlyError(e)) }
    finally { setPreviewLoading(false) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this view? This cannot be undone.')) return
    try {
      await api.deleteView(id)
      if (previewId === id) { setPreviewId(null); setPreviewData(null) }
      if (selectedId === id) setSelectedId('')
      await afterMutation()
    } catch (e: any) { setError(friendlyError(e)) }
  }

  return (
    <div style={{ maxWidth: 'var(--page-max, 1100px)', padding: '0 8px' }}>
      <header style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <LayoutList size={22} /> Custom views
          </h1>
          <button onClick={() => setCreating(true)} style={btnPrimary}>
            <Plus size={14} /> New view
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-text-soft)', margin: '6px 0 0 0' }}>
          Build custom prescription views with the columns, filters, and sort order your pharmacy needs.
          Each view execution is logged for HIPAA compliance.
        </p>
      </header>

      {error && <div style={errStyle}><AlertCircle size={14} /> {error}
        <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
      </div>}

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><Loader2 size={20} className="spin" /> Loading…</div>}

      {!loading && views.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-text-soft)' }}>
          <LayoutList size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ fontSize: 15 }}>No custom views yet</p>
          <p style={{ fontSize: 13 }}>Click "New view" to create your first custom prescription view.</p>
        </div>
      )}

      {/* CV-5: dropdown selector — pick a view, then act on it. Scales past the point where
          a flat card per view stops being scannable. */}
      {!loading && views.length > 0 && (() => {
        const selected = views.find(v => v.id === selectedId) ?? null
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600, maxWidth: 420 }}>
              Select a view ({views.length})
              <SearchableSelect
                value={selectedId}
                onChange={v => { setSelectedId(v); setPreviewId(null); setPreviewData(null) }}
                ariaLabel="Select a custom view"
                placeholder="Choose a view…"
                options={views.map(v => ({
                  value: v.id, label: v.name,
                  // P-VD-1: the identical-view badge shows IN the list too — a duplicate should
                  // be visible while choosing, not only after selecting it.
                  hint: `${v.column_count} cols · ${v.filter_count} filters${v.duplicate_of ? ` · ⚠ identical to “${v.duplicate_of.name}”` : ''}`,
                }))}
              />
            </label>
            {!selected && (
              <p style={{ fontSize: 12.5, color: 'var(--color-text-soft)', margin: 0 }}>
                Select a view to run, edit, or delete it.
              </p>
            )}
            {selected && (
              <div style={cardStyle}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selected.name}
                    {/* P-VD-1: computed badge — this view's definition is identical to an older one.
                        Self-healing: edit either view apart and it disappears on the next load. */}
                    {selected.duplicate_of && (
                      <span title={`Same columns, rules, visibility and sort as "${selected.duplicate_of.name}". Edit either view to differentiate them.`}
                        style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, border: '1px solid #f59e0b', background: '#fffbeb', color: '#92400e', whiteSpace: 'nowrap' }}>
                        Identical to “{selected.duplicate_of.name}”
                      </span>
                    )}
                  </div>
                  {selected.description && <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 2 }}>{selected.description}</div>}
                  <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginTop: 4 }}>
                    {selected.column_count} columns · {selected.filter_count} filters · Purpose: {selected.purpose_of_use}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditing(selected.id)} style={btnSmall} title="Edit view">
                    <Pencil size={13} /> Edit
                  </button>
                  <button onClick={() => executeView(selected.id)} style={btnSmall} title="Run view">
                    <Play size={13} /> Run
                  </button>
                  <button onClick={() => handleDelete(selected.id)} style={{ ...btnSmall, color: '#dc2626' }} title="Delete">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })()}

      {/* Preview panel */}
      {previewId && (
        <div style={{ marginTop: 16, border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Results {previewData ? `(${previewData.rows.length} rows, ${previewData.runtime_ms}ms)` : ''}
            </span>
            <button onClick={() => { setPreviewId(null); setPreviewData(null) }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
          {previewLoading && <div style={{ padding: 20, textAlign: 'center' }}><Loader2 size={16} className="spin" /> Executing…</div>}
          {previewData && previewData.rows.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-soft)' }}>No rows match the view's filters.</div>}
          {previewData && previewData.rows.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>{previewData.columns.map(c => <th key={c} style={thStyle}>{c}</th>)}</tr>
                </thead>
                <tbody>
                  {previewData.rows.map((row, i) => {
                    const drugCat = new Set(previewData.columns_meta
                      .filter(m => m.source_table === 'drug_reference' && m.column_name === 'category')
                      .map(m => m.label))
                    return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      {previewData.columns.map(c => <td key={c} style={tdStyle}>{
                        drugCat.has(c)
                          ? <CategoryBadge name={row[c] != null ? String(row[c]) : null}
                              color={row[c] != null ? catColors[String(row[c])] : null} />
                          : formatCell(c, row[c])
                      }</td>)}
                    </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit dialog */}
      {(creating || editing) && (
        <ViewDialog
          catalog={catalog}
          editView={editing ? views.find(v => v.id === editing) : undefined}
          editViewId={editing}
          views={views}
          onClose={() => { setCreating(false); setEditing(null) }}
          // CV-5: select what was just saved so the action card shows it immediately.
          onSaved={id => { setCreating(false); setEditing(null); if (id) setSelectedId(id); void afterMutation() }}
        />
      )}
    </div>
  )
}

// ── Operator config per data type ──────────────────────────────────
const OPS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
  string:  [{ value: 'eq', label: '=' }, { value: 'neq', label: '≠' }, { value: 'like', label: 'contains' }, { value: 'not_like', label: 'does not contain' }, { value: 'in', label: 'is any of' }, { value: 'not_in', label: 'is none of' }, { value: 'is_null', label: 'is empty' }, { value: 'is_not_null', label: 'is not empty' }],
  number:  [{ value: 'eq', label: '=' }, { value: 'neq', label: '≠' }, { value: 'gt', label: '>' }, { value: 'lt', label: '<' }, { value: 'gte', label: '≥' }, { value: 'lte', label: '≤' }, { value: 'between', label: 'between' }],
  date:    [{ value: 'eq', label: '=' }, { value: 'gt', label: 'after' }, { value: 'lt', label: 'before' }, { value: 'gte', label: 'on or after' }, { value: 'lte', label: 'on or before' }, { value: 'between', label: 'between' }],
  boolean: [{ value: 'eq', label: '=' }],
  guid:    [{ value: 'eq', label: '=' }],
}
const NO_VALUE_OPS = new Set(['is_null', 'is_not_null'])

interface RuleRow {
  fieldId: number
  operator: string
  value: string
  group: number
  conjunction: string
  /**
   * CV-4 RC-5 (UI half): the selected values for a multi-value operator, held as a REAL ARRAY.
   *
   * Multi-select used to round-trip through `value` as a comma-joined STRING and be split on
   * save. Any legitimate value containing a comma — "Refill, Welcome Call" is a real WorkBench
   * category — was silently torn into two meaningless filter terms, so the view filtered on
   * values that do not exist and returned nothing. The array is authoritative when present;
   * `value` stays in sync purely for display and for the free-text fallback, so a user typing
   * "a,b" by hand still works.
   */
  values?: string[]
}

/** The values a multi-value rule actually filters on: the array if present, else legacy comma-split. */
export function ruleValues(r: { value: string; values?: string[] }): string[] {
  if (r.values) return r.values
  return String(r.value ?? '').split(',').map(s => s.trim()).filter(Boolean)
}

/**
 * CV-4 RC-1: the AND/OR that a group ACTUALLY means, read from the rows whose selector the user
 * can see. The builder renders a connector only when the row is not first AND sits directly below
 * another row of the same group (`idx > 0 && sameGroupAsPrev`), so any other row's stored
 * `conjunction` is an unsettable leftover that must never decide the group.
 *
 * The first cut of this used `inGroup.slice(1)` — index WITHIN the filtered group — which diverges
 * from the render rule as soon as a group is non-contiguous. With rows [g0, g1, g0] the third row
 * has no visible connector, yet slice(1) treated its stale value as settable: exactly the
 * invisible-conjunction bug CV-4 was raised for. Key on physical adjacency, like the UI does.
 *
 * MIXED GROUPS FAIL NARROW: OR requires every settable connector in the group to say OR. A mixed
 * group has no single correct reading, and resolving it as OR would WIDEN the filter — the failure
 * mode that puts patients in a queue the pharmacist did not ask for. Mirrors the server exactly.
 */
export function settableConnectorRows(rules: RuleRow[], group: number): RuleRow[] {
  return rules.filter((r, idx) =>
    (r.group ?? 0) === group &&
    idx > 0 &&
    (rules[idx - 1].group ?? 0) === (r.group ?? 0))
}

export function groupConjunction(rules: RuleRow[], group: number): string {
  const settable = settableConnectorRows(rules, group)
  return settable.length > 0 && settable.every(r => r.conjunction === 'OR') ? 'OR' : 'AND'
}

/** True when a group's visible connectors disagree, so the builder can warn before saving. */
export function groupHasMixedConjunctions(rules: RuleRow[], group: number): boolean {
  const settable = settableConnectorRows(rules, group)
  return settable.length > 1 && new Set(settable.map(r => r.conjunction)).size > 1
}
// ASP-78: fieldId -> selectable values ([] means "free text", undefined means "not loaded yet").
type FieldOptions = Record<number, string[]>

export function ViewDialog({ catalog, editView, editViewId, onClose, onSaved, pharmacyId, views }: {
  catalog: api.ViewFieldCatalogItem[]
  editView?: api.PharmacyView
  editViewId?: string | null
  onClose: () => void
  /** CV-5: called with the saved view's id (create returns the new id; edit echoes its own). */
  onSaved: (id?: string) => void
  pharmacyId?: string
  /** P-VD-1: existing views for the create-mode "Load from existing" duplicator. */
  views?: api.PharmacyView[]
}) {
  const isEdit = !!editViewId
  const [name, setName] = useState(editView?.name || '')
  const [description, setDescription] = useState(editView?.description || '')
  const [purpose, setPurpose] = useState(editView?.purpose_of_use || '')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [rules, setRules] = useState<RuleRow[]>([])
  // ASP-78: lazily fetch the choice list for every field a rule references. Cached per
  // field id so switching a rule's field twice doesn't re-query.
  const [fieldOptions, setFieldOptions] = useState<FieldOptions>({})
  useEffect(() => {
    const wanted = Array.from(new Set(rules.map(r => r.fieldId))).filter(id => fieldOptions[id] === undefined)
    if (wanted.length === 0) return
    let cancelled = false
    Promise.all(wanted.map(id =>
      api.getViewFieldOptions(id, pharmacyId)
        .then(r => [id, r.options ?? []] as [number, string[]])
        .catch(() => [id, []] as [number, string[]])
    )).then(pairs => {
      if (cancelled) return
      setFieldOptions(prev => {
        const next = { ...prev }
        for (const [id, opts] of pairs) next[id] = opts
        return next
      })
    })
    return () => { cancelled = true }
  }, [rules, fieldOptions])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadingEdit, setLoadingEdit] = useState(false)
  // P-VD-1: which existing view (if any) the create form was seeded from.
  const [loadedFrom, setLoadedFrom] = useState('')
  const [users, setUsers] = useState<api.UserItem[]>([])
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set())
  const [colSearch, setColSearch] = useState('')

  // Load pharmacy users for RBAC
  useEffect(() => {
    if (pharmacyId) {
      api.listPharmacyUsers(pharmacyId)
        .then(r => setUsers((r.users || []).map((u: any) => ({ id: u.id ?? u.account_id, email: u.email ?? '', display_name: u.display_name ?? '', status: 'active', role_ids: null }))))
        .catch(() => {})
    } else {
      api.listUsers().then(r => setUsers(r.items.filter(u => u.status === 'active'))).catch(() => {})
    }
  }, [pharmacyId])

  // Prefill the whole form from a view's stored definition. Shared by edit mode and the
  // P-VD-1 "Load from existing" duplicator — one loader, no drift between the two paths.
  const applyDetail = useCallback((detail: api.ViewDetail) => {
    setName(detail.view.name)
    setDescription(detail.view.description || '')
    setPurpose(detail.view.purpose_of_use)
    setSelected(new Set(detail.columns.map(c => c.field_id)))
    setRules(detail.filters.map(f => ({
      fieldId: f.field_id,
      operator: f.operator,
      value: f.value_json ? (() => { try { const v = JSON.parse(f.value_json!); return Array.isArray(v) ? v.join(', ') : v } catch { return f.value_json! } })() : '',
      // CV-4 RC-5: keep the stored ARRAY intact on load. Re-deriving it from the joined string
      // would re-split any value that legitimately contains a comma — the round-trip is what
      // destroyed it before.
      values: f.value_json ? (() => { try { const v = JSON.parse(f.value_json!); return Array.isArray(v) ? v.map(String) : undefined } catch { return undefined } })() : undefined,
      group: (f as any).group_no ?? 0,
      conjunction: (f as any).conjunction ?? 'AND',
    })))
    setAssignedIds(new Set(detail.assignments.map(a => a.account_id)))
  }, [])

  // Load existing columns + filters when editing
  useEffect(() => {
    if (!isEdit || !editViewId) return
    setLoadingEdit(true)
    api.getView(editViewId, pharmacyId).then(applyDetail)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoadingEdit(false))
  }, [isEdit, editViewId, applyDetail, pharmacyId])

  // P-VD-1: duplicate an existing view — loads its FULL definition (name, purpose,
  // description, columns, rules, visibility) into the create form as a starting point.
  // Saving as-is creates an identical view, which the list then badges (computed
  // server-side at list time), so an accidental exact copy is visible, never silent.
  const loadFromExisting = (viewId: string) => {
    if (!viewId) return
    setLoadingEdit(true); setError(null)
    api.getView(viewId, pharmacyId).then(applyDetail)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoadingEdit(false))
  }

  const categories = Array.from(new Set(catalog.map(c => c.category || 'Other')))

  const toggle = (id: number) => {
    const next = new Set(selected)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelected(next)
  }

  const addRule = () => {
    const first = catalog[0]
    if (first) setRules([...rules, { fieldId: first.id, operator: 'eq', value: '', group: 0, conjunction: 'AND' }])
  }

  const updateRule = (idx: number, patch: Partial<RuleRow>) => {
    const next = [...rules]
    next[idx] = { ...next[idx], ...patch }
    // CV-4 RC-5: typing into the free-text box, or switching field/operator, invalidates a
    // chip-selected array — drop it so `ruleValues` falls back to parsing what was typed.
    // Without this, edits to the text box would be ignored in favour of a stale selection.
    if ((patch.value !== undefined && patch.values === undefined)
        || patch.fieldId !== undefined || patch.operator !== undefined)
      next[idx].values = patch.values

    // Reset operator if field type changed
    if (patch.fieldId !== undefined) {
      const field = catalog.find(f => f.id === patch.fieldId)
      const ops = OPS_BY_TYPE[field?.data_type || 'string'] || OPS_BY_TYPE.string
      if (!ops.find(o => o.value === next[idx].operator)) next[idx].operator = ops[0].value
    }
    setRules(next)
  }

  const removeRule = (idx: number) => setRules(rules.filter((_, i) => i !== idx))

  const submit = async () => {
    if (!name.trim() || !purpose.trim() || selected.size === 0) {
      setError('Name, purpose, and at least one column are required.'); return
    }
    setBusy(true); setError(null)
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        purpose_of_use: purpose.trim(),
        columns: Array.from(selected).map(id => ({ field_id: id })),
        filters: rules.map(r => ({
          field_id: r.fieldId,
          operator: r.operator,
          // CV-4 RC-5: send the real array. `ruleValues` prefers the tracked array and only falls
          // back to comma-splitting for a hand-typed value, so a selected option containing a
          // comma survives instead of being torn into terms that match nothing.
          value_json: NO_VALUE_OPS.has(r.operator) ? undefined
            : (r.operator === 'in' || r.operator === 'not_in' || r.operator === 'between')
              ? JSON.stringify(ruleValues(r))
              : JSON.stringify(r.value),
          group_no: r.group ?? 0,
          // CV-4 RC-1: stamp the group's conjunction onto EVERY row of that group, including the
          // first — whose selector this UI never renders (`idx > 0 && sameGroupAsPrev`) and whose
          // stored value the executor used to obey. Deleting the first rule of a group also used
          // to promote a row whose 'OR' then became invisible, so a view reading "A AND B" could
          // execute as "A OR B" and return the wrong patients. Normalising on save means the
          // stored rows can never disagree with what the builder displays.
          conjunction: groupConjunction(rules, r.group ?? 0),
        })),
      }
      if (isEdit) {
        await api.updateView(editViewId!, body, pharmacyId)
        await api.updateViewAssignments(editViewId!, Array.from(assignedIds), pharmacyId)
        onSaved(editViewId!)
      } else {
        const result = await api.createView(body, pharmacyId)
        if (assignedIds.size > 0) await api.updateViewAssignments(result.id, Array.from(assignedIds), pharmacyId)
        onSaved(result.id)
      }
    } catch (e: any) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ ...dialogStyle, maxWidth: 640 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '0 0 12px' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
            {isEdit ? 'Edit view' : 'Create custom view'}
          </h2>
          {/* P-VD-1: duplicate-a-view. Create mode only — picks an existing view and prefills
              the entire form; the user then tweaks columns/rules/visibility and creates. */}
          {!isEdit && (views?.length ?? 0) > 0 && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-soft)' }}>
              Load existing view
              <SearchableSelect value={loadedFrom} onChange={v => { setLoadedFrom(v); loadFromExisting(v) }}
                ariaLabel="Load an existing view as a starting point"
                options={[{ value: '', label: 'Choose a view…' }, ...views!.map(v => ({ value: v.id, label: v.name }))]}
                style={{ minWidth: 180 }} />
            </label>
          )}
        </div>

        {loadingEdit && <div style={{ padding: 20, textAlign: 'center', fontSize: 13 }}>Loading view…</div>}
        {!loadingEdit && (<>

        <label style={labelStyle}>View name *</label>
        <input value={name} onChange={e => setName(e.target.value)} style={inputStyle}
          placeholder="e.g. Today's intake queue" />

        <label style={labelStyle}>Purpose of use *</label>
        <input value={purpose} onChange={e => setPurpose(e.target.value)} style={inputStyle}
          placeholder="e.g. Intake team — daily processing" />
        <p style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: '2px 0 8px' }}>
          Required for HIPAA compliance.
        </p>

        <label style={labelStyle}>Description</label>
        <input value={description} onChange={e => setDescription(e.target.value)} style={inputStyle} placeholder="Optional" />

        {/* ── Column picker ── */}
        <label style={{ ...labelStyle, marginTop: 12 }}>Columns ({selected.size} selected)</label>
        <input
          value={colSearch}
          onChange={e => setColSearch(e.target.value)}
          placeholder="Search fields…"
          aria-label="Search fields"
          style={{ ...inputStyle, marginBottom: 6 }}
        />
        <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 4, padding: 8, marginBottom: 12 }}>
          {(() => {
            const needle = colSearch.trim().toLowerCase()
            const match = (f: api.ViewFieldCatalogItem) => !needle
              || f.display_label.toLowerCase().includes(needle)
              || (f.category || 'Other').toLowerCase().includes(needle)
            // D-CV-7 A.2: a hidden entry is shown only while it is selected (so it can be removed);
            // it is never offered for a new pick. The server says why in hidden_reason.
            const visible = catalog.filter(match).filter(f => !f.hidden || selected.has(f.id))
            if (visible.length === 0) return (
              <div style={{ fontSize: 12, color: 'var(--color-text-mute)', padding: '6px 4px' }}>
                No fields match "{colSearch}".
              </div>
            )
            return categories.filter(cat => visible.some(c => (c.category || 'Other') === cat)).map(cat => (
            <div key={cat}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-mute)', padding: '4px 0 2px', textTransform: 'uppercase' }}>{cat}</div>
              {visible.filter(c => (c.category || 'Other') === cat).map(f => (
                <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px', cursor: 'pointer', fontSize: 12 }}>
                  <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggle(f.id)} />
                  {f.display_label}
                  {f.source_hint && (
                    <span title={`Two columns share this label; this one comes from ${f.source_hint}.`}
                      style={{ fontSize: 10.5, color: 'var(--color-text-mute)' }}>· {f.source_hint}</span>
                  )}
                  {f.hidden && (
                    <span title={f.hidden_reason ?? undefined} style={{ fontSize: 9, padding: '0px 4px', borderRadius: 3, background: 'var(--color-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-mute)' }}>
                      {f.duplicate_of ? 'duplicate' : 'not available'}
                    </span>
                  )}
                  {(f.sensitivity === 'phi' || f.sensitivity === 'phi_high') && (
                    <span style={{ fontSize: 9, padding: '0px 4px', borderRadius: 3,
                      background: f.sensitivity === 'phi_high' ? '#fef2f2' : '#fef9c3',
                      color: f.sensitivity === 'phi_high' ? '#991b1b' : '#854d0e',
                    }}>{f.sensitivity === 'phi_high' ? 'PHI-HIGH' : 'PHI'}</span>
                  )}
                </label>
              ))}
            </div>
            ))
          })()}
        </div>

        {/* ── Filter rules (workqueue logic) ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <label style={labelStyle}>Rules — what should this view show? ({rules.length})</label>
          <button onClick={addRule} style={{ fontSize: 11, padding: '3px 8px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer' }}>+ Add rule</button>
        </div>
        {rules.length === 0 && (
          <p style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: '0 0 8px' }}>
            No rules — view will show all prescriptions. Add rules to narrow it.
            Rules in the same group combine with AND/OR; different groups are
            AND-joined — e.g. (Status = Pending OR Status = Reviewing) AND Payer = Commercial.
          </p>
        )}
        {Array.from(new Set(rules.map(r => r.group ?? 0)))
          .filter(g => groupHasMixedConjunctions(rules, g))
          .map(g => (
            <p key={`mixed-${g}`} role="alert" style={{
              fontSize: 11, margin: '0 0 6px', padding: '6px 8px', borderRadius: 4,
              background: 'var(--color-warn-bg, #fff4e5)', color: 'var(--color-warn-text, #7a4a00)',
            }}>
              <strong>Grp {g + 1}</strong> mixes AND and OR. A group runs as one or the other, and a
              mixed group runs as <strong>AND</strong> so it never returns more patients than you
              asked for. Set every connector in the group to OR if you meant OR.
            </p>
          ))}
        {rules.map((rule, idx) => {
          const field = catalog.find(f => f.id === rule.fieldId)
          const opts = fieldOptions[rule.fieldId]
          const ops = OPS_BY_TYPE[field?.data_type || 'string'] || OPS_BY_TYPE.string
          const needsValue = !NO_VALUE_OPS.has(rule.operator)
          const multi = rule.operator === 'in' || rule.operator === 'not_in'
          const between = rule.operator === 'between'
          const prevGroup = idx > 0 ? rules[idx - 1].group : rule.group
          const sameGroupAsPrev = idx > 0 && prevGroup === rule.group
          return (
            <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {idx > 0 && (
                sameGroupAsPrev
                  ? <select value={rule.conjunction} onChange={e => updateRule(idx, { conjunction: e.target.value })}
                      style={{ ...ruleSelect, width: 62, fontWeight: 700 }} aria-label="Combine with previous rule">
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </select>
                  : <span style={{ fontSize: 10, color: 'var(--color-text-mute)', fontWeight: 700 }}>AND</span>
              )}
              <select value={rule.group} onChange={e => updateRule(idx, { group: parseInt(e.target.value) })}
                style={{ ...ruleSelect, width: 74 }} title="Rules in the same group combine by AND/OR; groups are joined by AND" aria-label="Group">
                {[0,1,2,3,4].map(g => <option key={g} value={g}>Grp {g + 1}</option>)}
              </select>
              <select value={rule.fieldId} onChange={e => updateRule(idx, { fieldId: parseInt(e.target.value) })} style={ruleSelect}>
                {catalog.map(f => <option key={f.id} value={f.id}>{f.display_label}</option>)}
              </select>
              <select value={rule.operator} onChange={e => updateRule(idx, { operator: e.target.value })} style={{ ...ruleSelect, width: 110 }}>
                {ops.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {needsValue && (
                // ASP-78: when the field has a known choice list, pick from it instead of
                // typing the exact stored string. Multi-value operators (in / not_in) get
                // checkboxes; single-value gets a dropdown. Fields with no list (free text,
                // dates, numbers, or >200 distinct values) keep the original input.
                opts && opts.length > 0 && !between
                  ? (multi
                      ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1, minWidth: 160, maxHeight: 84, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 4, padding: 4 }}>
                          {opts.map(o => {
                            // CV-4 RC-5: selection is tracked as an ARRAY. `value` is kept in sync
                            // for display only — it is no longer the source of truth, so an option
                            // containing a comma round-trips intact.
                            const sel = ruleValues(rule)
                            const on = sel.includes(o)
                            return (
                              <button
                                key={o}
                                type="button"
                                onClick={() => {
                                  const next = on ? sel.filter(x => x !== o) : [...sel, o]
                                  updateRule(idx, { values: next, value: next.join(', ') })
                                }}
                                style={{
                                  fontSize: 11, padding: '2px 8px', borderRadius: 999, cursor: 'pointer',
                                  border: '1px solid ' + (on ? 'var(--color-primary, #0d9488)' : 'var(--color-border)'),
                                  background: on ? 'var(--color-primary, #0d9488)' : 'transparent',
                                  color: on ? '#fff' : 'var(--color-text)',
                                }}
                              >{o}</button>
                            )
                          })}
                        </div>
                      : <select
                          value={rule.value}
                          onChange={e => updateRule(idx, { value: e.target.value })}
                          style={{ ...ruleSelect, flex: 1, minWidth: 120 }}
                        >
                          <option value="">Select a value…</option>
                          {opts.map(o => <option key={o} value={o}>{o}</option>)}
                        </select>)
                  : <input
                      value={rule.value}
                      onChange={e => updateRule(idx, { value: e.target.value })}
                      placeholder={multi ? 'a, b, c' : between ? 'low, high' : field?.data_type === 'date' ? 'YYYY-MM-DD' : 'value'}
                      style={{ ...inputStyle, marginBottom: 0, flex: 1, minWidth: 100 }}
                    />
              )}
              <button onClick={() => removeRule(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 14, padding: '0 4px' }}>×</button>
            </div>
          )
        })}

        {error && <div style={{ ...errStyle, marginTop: 8 }}><AlertCircle size={12} /> {error}</div>}

        {/* ── RBAC — who sees this view? ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, marginBottom: 6 }}>
          <label style={labelStyle}>Visible to ({assignedIds.size === 0 ? 'everyone' : assignedIds.size + ' users'})</label>
        </div>
        <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 4, padding: 8, marginBottom: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            <input type="checkbox" checked={assignedIds.size === 0}
              onChange={() => setAssignedIds(new Set())} />
            Everyone in the pharmacy
          </label>
          {users.map(u => (
            <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px', cursor: 'pointer', fontSize: 12 }}>
              <input type="checkbox" checked={assignedIds.has(u.id)}
                onChange={() => {
                  const next = new Set(assignedIds)
                  next.has(u.id) ? next.delete(u.id) : next.add(u.id)
                  setAssignedIds(next)
                }} />
              {u.display_name || u.email}
              <span className="email-text" style={{ fontSize: 10, color: 'var(--color-text-mute)', marginLeft: 'auto' }}>{u.email}</span>
            </label>
          ))}
        </div>
        <p style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: '0 0 8px' }}>
          If "Everyone" is checked, all pharmacy users see this view as a tab. Otherwise only selected users.
        </p>

        </>)}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button onClick={onClose} disabled={busy} style={btnCancel}>Cancel</button>
          <button onClick={submit} disabled={busy} style={btnPrimary}>
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create view'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Styles
const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 14px',
  background: 'var(--color-accent, #2563eb)', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer',
}
const btnSmall: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 10px',
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 4, fontSize: 12, cursor: 'pointer',
}
const btnCancel: React.CSSProperties = {
  padding: '7px 14px', background: 'transparent', border: '1px solid var(--color-border)',
  borderRadius: 4, fontSize: 13, color: 'var(--color-text-soft)', cursor: 'pointer',
}
const cardStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px', border: '1px solid var(--color-border)', borderRadius: 6,
  background: 'var(--color-surface)',
}
const thStyle: React.CSSProperties = {
  padding: '6px 10px', textAlign: 'left', fontWeight: 600,
  borderBottom: '2px solid var(--color-border)', whiteSpace: 'nowrap',
  background: 'var(--color-surface)',
}
const tdStyle: React.CSSProperties = {
  padding: '5px 10px', whiteSpace: 'nowrap', maxWidth: 200,
  overflow: 'hidden', textOverflow: 'ellipsis',
}
const errStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
  background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4,
  color: '#991b1b', fontSize: 13, marginBottom: 12,
}
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
  justifyContent: 'center', alignItems: 'center', zIndex: 1000,
}
const dialogStyle: React.CSSProperties = {
  background: 'var(--color-bg, #fff)', borderRadius: 8, padding: 24,
  width: '95%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4,
  color: 'var(--color-text)',
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 10px', border: '1px solid var(--color-border)',
  borderRadius: 4, fontSize: 13, marginBottom: 8, boxSizing: 'border-box',
}
const ruleSelect: React.CSSProperties = {
  padding: '6px 8px', border: '1px solid var(--color-border)',
  borderRadius: 4, fontSize: 12, background: 'var(--color-surface)',
}
